import * as R from 'ramda'

import React, { createContext, useContext, useEffect, useState } from 'react'

import useAxios from 'hooks/useAxios'

export const navBarMenuContext = createContext()

export const useNavbarMenuContext = () => useContext(navBarMenuContext)

export const NavbarContextProvider = ({ children }) => {
  const [contextData, setContextData] = useState({
    surveyDashboardViews: null,
    customDashboards: null,
    companyUsers: [],
    customDashboardsLoading: false,
    surveyDashboardViewsLoading: false,
    companyUsersLoading: false,
    feedbackViewPages: { results: [] },
    feedbackViewPagesLoading: false,
    favoriteFeedbackPages: { results: [] },
    favoriteFeedbackPagesLoading: false,
    favoriteDashboardSurveyPages: { results: [] },
    favoriteDashboardSurveyPagesLoading: false,
    surveyDashboardViewsReachLimit: false,
    teamMemberGroups: [],
    teamMemberGroupsLoading: false,
  })

  const { data: feedbackViewPages } = useAxios({
    url: 'dashboard/feedback-view-pages/',
  })

  const { data: favoriteFeedbackPages } = useAxios({
    url: 'dashboard/list-favorite-view/?type=feedback',
  })
  const { data: favoriteDashboardSurveyPages } = useAxios({
    url: 'dashboard/list-favorite-view/?type=dashboard&type=survey',
  })

  const { fetch: getViews, data: views } = useAxios({
    url: `/survey/views/?limit=20`,
  })

  const { data: dashboards } = useAxios({
    url: 'dashboard/dashboards/',
  })

  const { data: userAccess } = useAxios({
    url: 'dashboard/dashboards/user-access/',
  })

  const { data: teamMemberGroupsData } = useAxios({
    url: 'team-members/groups/',
  })

  const createContextData = (name, obj) => {
    if (name === 'dashboard') {
      setContextData((prev) => ({
        ...prev,
        customDashboards: {
          ...prev.customDashboards,
          results: [...prev.customDashboards.results, obj],
        },
      }))
    } else if (name === 'feedback') {
      setContextData((prev) => ({
        ...prev,
        feedbackViewPages: {
          results: [...prev.feedbackViewPages.results, obj],
        },
      }))
    }
  }

  const updateContextData = (name, obj) => {
    if (name === 'dashboard') {
      setContextData((prev) => ({
        ...prev,
        customDashboards: {
          ...prev.customDashboards,
          results: prev.customDashboards.results.map((dashboard) =>
            dashboard.id === obj.id ? { ...dashboard, ...obj } : dashboard
          ),
        },
      }))
    } else if (name === 'favorite') {
      setContextData((prev) => {
        const hasTokenInFavoriteList = prev.favoriteDashboardSurveyPages.results.some(
          (fav) => fav.token === obj.token
        )
        const isSetFavorite = obj.flagged

        if (isSetFavorite && !hasTokenInFavoriteList) {
          return {
            ...prev,
            favoriteDashboardSurveyPages: {
              results: R.pipe(
                R.append(obj),
                R.sortBy(R.prop('name'))
              )(prev.favoriteDashboardSurveyPages.results),
            },
          }
        } else if (!isSetFavorite && hasTokenInFavoriteList) {
          return {
            ...prev,
            favoriteDashboardSurveyPages: {
              results: prev.favoriteDashboardSurveyPages.results.filter(
                (fav) => fav.token !== obj.token
              ),
            },
          }
        } else {
          return prev
        }
      })
    } else if (name == 'feedback') {
      setContextData((prev) => {
        const updatedFeedbackViews = prev.feedbackViewPages.results.map((item) => {
          if (item.token === obj.token) {
            return { ...item, ...obj }
          }
          return item
        })
        return {
          ...prev,
          feedbackViewPages: {
            results: updatedFeedbackViews,
          },
        }
      })
    } else if (name == 'survey') {
      setContextData((prev) => ({
        ...prev,
        surveyDashboardViews: obj,
        surveyDashboardViewsReachLimit: false,
      }))
    } else {
      getViews()
    }
  }

  const deleteContextData = (name, token) => {
    if (name === 'dashboard') {
      setContextData((prev) => ({
        ...prev,
        customDashboards: {
          ...prev.customDashboards,
          results: prev.customDashboards.results.filter((item) => item.token !== token),
        },
      }))
    }
  }

  useEffect(() => {
    if (views) {
      setContextData((prev) => ({
        ...prev,
        surveyDashboardViews: R.path(['results'], views) || null,
        surveyDashboardViewsLoading: false,
        surveyDashboardViewsReachLimit: R.path(['count'], views) > 5,
      }))
    } else {
      setContextData((prev) => ({
        ...prev,
        surveyDashboardViewsLoading: true,
        surveyDashboardViewsReachLimit: false,
      }))
    }
  }, [views])

  useEffect(() => {
    if (feedbackViewPages) {
      setContextData((prev) => ({
        ...prev,
        feedbackViewPages: feedbackViewPages,
        feedbackViewPagesLoading: false,
      }))
    } else {
      setContextData((prev) => ({ ...prev, feedbackViewPagesLoading: true }))
    }
  }, [feedbackViewPages])

  useEffect(() => {
    if (favoriteFeedbackPages) {
      setContextData((prev) => ({
        ...prev,
        favoriteFeedbackPages,
        favoriteFeedbackPagesLoading: false,
      }))
    } else {
      setContextData((prev) => ({ ...prev, favoriteFeedbackPagesLoading: true }))
    }
  }, [favoriteFeedbackPages])

  useEffect(() => {
    if (favoriteDashboardSurveyPages) {
      setContextData((prev) => ({
        ...prev,
        favoriteDashboardSurveyPages,
        favoriteDashboardSurveyPagesLoading: false,
      }))
    } else {
      setContextData((prev) => ({ ...prev, favoriteDashboardSurveyPagesLoading: true }))
    }
  }, [favoriteDashboardSurveyPages])

  useEffect(() => {
    if (dashboards) {
      setContextData((prev) => ({
        ...prev,
        customDashboards: dashboards,
        customDashboardsLoading: false,
      }))
    } else {
      setContextData((prev) => ({ ...prev, customDashboardsLoading: true }))
    }
  }, [dashboards])

  useEffect(() => {
    if (userAccess) {
      setContextData((prev) => ({
        ...prev,
        companyUsers: userAccess.allowed_user_access,
        companyUsersLoading: false,
      }))
    } else {
      setContextData((prev) => ({ ...prev, companyUsersLoading: true }))
    }
  }, [userAccess])

  useEffect(() => {
    if (teamMemberGroupsData) {
      setContextData((prev) => ({
        ...prev,
        teamMemberGroups: teamMemberGroupsData.results || [],
        teamMemberGroupsLoading: false,
      }))
    } else {
      setContextData((prev) => ({ ...prev, teamMemberGroupsLoading: true }))
    }
  }, [teamMemberGroupsData])

  return (
    <navBarMenuContext.Provider
      value={{
        contextData: contextData,
        setContextData: setContextData,
        createContextData: createContextData,
        updateContextData: updateContextData,
        deleteContextData: deleteContextData,
      }}
    >
      {children}
    </navBarMenuContext.Provider>
  )
}

export default NavbarContextProvider



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/organisms/NavBar/context/index.js